import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const DEFAULT_HTML = "<p><br></p>";

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

/**
 * Rich HTML editor using Quill directly (compatible with React 19 — avoids react-quill's findDOMNode).
 */
const CartRecoveryHtmlEditor = ({ value, onChange, className = "" }) => {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const skipExternalSyncRef = useRef(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const quill = new Quill(host, {
      theme: "snow",
      modules: {
        toolbar: TOOLBAR_OPTIONS,
      },
    });
    quillRef.current = quill;

    const initial = value && value.trim() ? value : DEFAULT_HTML;
    quill.clipboard.dangerouslyPasteHTML(initial);

    const handleTextChange = () => {
      skipExternalSyncRef.current = true;
      onChangeRef.current(quill.root.innerHTML);
    };

    quill.on("text-change", handleTextChange);

    return () => {
      quill.off("text-change", handleTextChange);
      quillRef.current = null;
      host.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    if (skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false;
      return;
    }

    const next = value && value.trim() ? value : DEFAULT_HTML;
    const current = quill.root.innerHTML;
    if (next === current) return;

    const selection = quill.getSelection();
    quill.clipboard.dangerouslyPasteHTML(next);
    if (selection) {
      try {
        quill.setSelection(selection);
      } catch {
        // ignore invalid range after replace
      }
    }
  }, [value]);

  return (
    <div
      ref={hostRef}
      className={className}
      role="textbox"
      aria-multiline="true"
    />
  );
};

export default CartRecoveryHtmlEditor;
